import React, { useState, useEffect } from 'react';
import { useAlert } from '../context/AlertContext';
import api from '../services/api';
import { Plus, Trash2, Edit, Clock, DollarSign } from 'lucide-react';
import { Table, Button, Modal, Form, Row, Col, Card } from 'react-bootstrap';
import './ProfessionalServiceManager.css';

interface Service {
    id: number;
    name: string;
    duration: string;
    price: string;
}

interface ProfessionalService {
    id: number;
    professional: number;
    service: number;
    service_name: string;
    service_duration: string;
    default_price: string;
    custom_price: string;
}

interface ProfessionalServiceManagerProps {
    professionalId: number;
}

const ProfessionalServiceManager: React.FC<ProfessionalServiceManagerProps> = ({ professionalId }) => {
    const { showAlert } = useAlert();
    const [professionalServices, setProfessionalServices] = useState<ProfessionalService[]>([]);
    const [allServices, setAllServices] = useState<Service[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState<ProfessionalService | null>(null);
    const [formData, setFormData] = useState({
        service: '',
        custom_price: ''
    });

    useEffect(() => {
        if (professionalId) {
            fetchProfessionalServices();
            fetchAllServices();
        }
    }, [professionalId]);

    const fetchProfessionalServices = async () => {
        try {
            const response = await api.get(`/professional-services/?professional=${professionalId}`);
            setProfessionalServices(response.data);
        } catch (error) {
            console.error('Error fetching professional services:', error);
        }
    };

    const fetchAllServices = async () => {
        try {
            const response = await api.get('/services/');
            setAllServices(response.data);
        } catch (error) {
            console.error('Error fetching services:', error);
        }
    };

    const handleOpenModal = (service?: ProfessionalService) => {
        if (service) {
            setEditingService(service);
            setFormData({
                service: service.service.toString(),
                custom_price: service.custom_price
            });
        } else {
            setEditingService(null);
            setFormData({
                service: '',
                custom_price: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingService(null);
        setFormData({
            service: '',
            custom_price: ''
        });
    };

    const handleServiceChange = (serviceId: string) => {
        setFormData({ ...formData, service: serviceId });

        // Auto-fill custom_price with default price
        const selectedService = allServices.find(s => s.id.toString() === serviceId);
        if (selectedService && !editingService) {
            setFormData({
                service: serviceId,
                custom_price: selectedService.price
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        try {
            const submitData = {
                professional: professionalId,
                service: parseInt(formData.service),
                custom_price: formData.custom_price
            };

            if (editingService) {
                await api.put(`/professional-services/${editingService.id}/`, submitData);
            } else {
                await api.post('/professional-services/', submitData);
            }

            fetchProfessionalServices();
            handleCloseModal();
        } catch (error: any) {
            console.error('Error saving professional service:', error);
            if (error.response?.data?.non_field_errors) {
                showAlert('Este serviço já está associado a este profissional.', {
                    title: 'Erro',
                    type: 'error'
                });
            } else {
                showAlert('Erro ao salvar serviço.', {
                    title: 'Erro',
                    type: 'error'
                });
            }
        }
    };

    const handleDelete = (id: number) => {
        showAlert('Tem certeza que deseja remover este serviço do profissional?', {
            title: 'Confirmação',
            confirmable: true,
            confirmText: 'Remover',
            confirmVariant: 'btn-primary',
            onConfirm: async () => {
                try {
                    await api.delete(`/professional-services/${id}/`);
                    fetchProfessionalServices();
                } catch (error) {
                    console.error('Error deleting professional service:', error);
                }
            }
        });
    };

    // Filter out services that are already assigned
    const availableServices = allServices.filter(
        service => !professionalServices.some(ps => ps.service === service.id)
    );

    return (
        <Card className="shadow-sm border-0 mt-4">
            <Card.Body>
                <Row className="mb-3 align-items-center">
                    <Col>
                        <h5 className="mb-0">Serviços do Profissional</h5>
                    </Col>
                    <Col xs="auto">
                        <Button
                            variant="success"
                            onClick={() => handleOpenModal()}
                            className="d-flex align-items-center gap-2"
                            size="sm"
                        >
                            <Plus size={18} /> Adicionar Serviço
                        </Button>
                    </Col>
                </Row>

                {/* Desktop: Tabela */}
                <div className="custom-table-container d-none d-md-block">
                    <Table hover className="custom-table mb-0">
                        <thead>
                            <tr>
                                <th>Serviço</th>
                                <th>Duração</th>
                                <th>Preço Padrão</th>
                                <th>Preço Customizado</th>
                                <th className="text-end">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {professionalServices.map((ps) => (
                                <tr
                                    key={ps.id}
                                    onClick={() => handleOpenModal(ps)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td className="fw-medium">{ps.service_name}</td>
                                    <td>{ps.service_duration}</td>
                                    <td>R$ {ps.default_price}</td>
                                    <td className="fw-bold text-primary">R$ {ps.custom_price}</td>
                                    <td className="text-end">
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1 me-2"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleOpenModal(ps);
                                            }}
                                        >
                                            <Edit size={18} />
                                        </Button>
                                        <Button
                                            variant="link"
                                            className="text-secondary p-1"
                                            onClick={(e: React.MouseEvent) => {
                                                e.stopPropagation();
                                                handleDelete(ps.id);
                                            }}
                                        >
                                            <Trash2 size={18} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {professionalServices.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="text-center py-4 text-muted">
                                        Nenhum serviço associado. Clique em "Adicionar Serviço" para começar.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>

                {/* Mobile: Lista de Cards */}
                <div className="prof-service-list-mobile d-md-none">
                    {professionalServices.length > 0 ? (
                        professionalServices.map((ps) => (
                            <div key={ps.id} className="prof-service-card-mobile">
                                <div 
                                    className="prof-service-card-mobile-content"
                                    onClick={() => handleOpenModal(ps)}
                                >
                                    <div className="prof-service-card-mobile-info">
                                        <span className="prof-service-card-mobile-name">{ps.service_name}</span>
                                        <div className="prof-service-card-mobile-details">
                                            <span className="prof-service-card-mobile-detail">
                                                <Clock size={14} /> {ps.service_duration}
                                            </span>
                                            <span className="prof-service-card-mobile-detail prof-service-card-mobile-price">
                                                <DollarSign size={14} /> R$ {ps.custom_price}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="prof-service-card-mobile-actions">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="prof-service-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleOpenModal(ps);
                                        }}
                                    >
                                        <Edit size={16} /> Editar
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        className="prof-service-card-mobile-btn"
                                        onClick={(e: React.MouseEvent) => {
                                            e.stopPropagation();
                                            handleDelete(ps.id);
                                        }}
                                    >
                                        <Trash2 size={16} /> Excluir
                                    </Button>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4 text-muted">
                            Nenhum serviço associado. Clique em "Adicionar Serviço" para começar.
                        </div>
                    )}
                </div>
            </Card.Body>

            {/* Modal for Add/Edit Service */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingService ? 'Editar Serviço' : 'Adicionar Serviço'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Serviço</Form.Label>
                            <Form.Select
                                value={formData.service}
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleServiceChange(e.target.value)}
                                required
                                disabled={!!editingService}
                            >
                                <option value="">Selecione um serviço...</option>
                                {editingService ? (
                                    <option value={editingService.service}>
                                        {editingService.service_name}
                                    </option>
                                ) : (
                                    availableServices.map(service => (
                                        <option key={service.id} value={service.id}>
                                            {service.name} - R$ {service.price}
                                        </option>
                                    ))
                                )}
                            </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Preço Customizado</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={formData.custom_price}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, custom_price: e.target.value })}
                                required
                                placeholder="0.00"
                            />
                            <Form.Text className="text-muted">
                                Este é o preço que este profissional cobra por este serviço.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit">
                            {editingService ? 'Atualizar' : 'Adicionar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Card>
    );
};

export default ProfessionalServiceManager;
